<template>
  <section>
    <!-- breadcrumb -->
    <header class="breadcrumb">
      <span>Devices archiviati</span>
      <div class="breadcrumb-actions">
        <RouterLink class="btn-new" :to="{ name: 'Devices' }"
          >⤺ Torna ai devices</RouterLink
        >
        <NotificationBell v-if="showCrumbBell" placement="header" />
      </div>
    </header>

    <!-- messaggi -->
    <p v-if="loading" class="msg">Caricamento…</p>
    <p v-else-if="error" class="msg error">
      {{ error }}
    </p>
    <p v-else-if="!archived.length" class="msg">Nessun device archiviato</p>

    <div v-else class="dashboard-wrapper">
      <!-- Gmail-style Selection Bar -->
      <Transition name="slide-fade">
        <div v-if="selectedIds.length > 0" class="selection-bar">
          <div class="selection-info">
            <button
              class="btn-close-selection"
              @click="selectedIds = []"
              title="Annulla selezione"
            >
              <XIcon />
            </button>
            <span class="selection-count">{{ selectedIds.length }} selezionati</span>
          </div>
          <div class="selection-actions">
            <button
              class="btn-selection-action restore"
              @click="openBulkRestoreModal"
              title="Ripristina selezionati"
            >
              <ArchiveRestoreIcon />
              <span>Ripristina</span>
            </button>
          </div>
        </div>
      </Transition>

      <div v-show="archived.length" 
           class="tbl-wrap"
           :style="{ marginTop: selectedIds.length > 0 ? '10px' : '50px' }"
      >
      <table class="tbl">
        <thead>
          <tr>
            <th class="select-col">
              <input
                type="checkbox"
                :checked="isAllSelected"
                @change="toggleSelectAll"
                :title="isAllSelected ? 'Deseleziona tutti i device' : 'Seleziona tutti i device'"
              />
            </th>
            <th @click="toggleSort('id')" class="sortable">
              <div class="header-content">
                ID
                <component 
                  :is="sortBy === 'id' ? (sortOrder === 'asc' ? ChevronUpIcon : ChevronDownIcon) : 'span'" 
                  class="sort-icon" 
                />
              </div>
            </th>
            <th>Nome</th>
            <th>Status</th>
            <th>Moduli</th>
            <th @click="toggleSort('updatedAt')" class="sortable">
              <div class="header-content">
                Last Update
                <component 
                  :is="sortBy === 'updatedAt' ? (sortOrder === 'asc' ? ChevronUpIcon : ChevronDownIcon) : 'span'" 
                  class="sort-icon" 
                />
              </div>
            </th>
            <th v-if="isAdmin">Proprietario</th>
            <th v-if="isAdmin" class="right"></th>
          </tr>
        </thead>
        <tbody>
          <!-- Riga di ricerca -->
          <tr class="search-row">
            <td :colspan="isAdmin ? 8 : 6">
              <div class="search-container">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Cerca per ID, Nome o Status..."
                  class="search-input"
                />
              </div>
            </td>
          </tr>
          <tr v-for="(d, i) in paginatedArchived" :key="d.id" :class="i % 2 && 'alt'">
            <td class="select-col">
              <input
                type="checkbox"
                :value="d.id"
                v-model="selectedIds"
                :title="selectedIds.includes(d.id) ? 'Deseleziona questo device' : 'Seleziona questo device'"
              />
            </td>
            <td class="mono">{{ d.id }}</td>
            <td>{{ d.name || "—" }}</td>
            <td>
              <span class="status-dot" :class="getStatusClass(d.status)" />
              {{ d.status || "—" }}
            </td>
            <td>{{ listModules(d) }}</td>
            <td>{{ formatDate(d.updatedAt) }}</td>
            <!-- ⬇️ badge proprietario, visibile solo agli admin -->
            <td v-if="isAdmin">
              <span
                class="owner-badge"
                :class="d.user?.is_admin ? 'admin' : 'user'"
              >
                <span class="owner-role">{{
                  d.user?.is_admin ? "Admin" : "User"
                }}</span>
                <span class="owner-sep">•</span>
                <span class="owner-name">{{ ownerDisplayName(d) }}</span>
                <span class="owner-id">(#{{ d.user?.id ?? d.user_id }})</span>
              </span>
            </td>
            <td v-if="isAdmin" class="actions right">
              <button
                v-if="selectedIds.length === 0"
                class="restore-icon"
                title="Ripristina"
                @click="openRestoreModal(d)"
              >
                <ArchiveRestoreIcon />
              </button>
            </td>
          </tr>
          <!-- Riga nessun risultato -->
          <tr v-if="searchQuery && !filteredArchived.length">
            <td :colspan="isAdmin ? 8 : 7" class="msg-row" style="text-align: center; padding: 24px; color: #666; background: #fff;">
              Nessun risultato trovato per "{{ searchQuery }}"
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginazione -->
    <div class="pagination-controls" v-if="totalPages > 1">
      <button
        class="btn-pagination"
        :disabled="currentPage === 1"
        @click="currentPage = 1"
        title="Vai alla prima pagina"
      >
        <FontAwesomeIcon :icon="faAnglesLeft" />
      </button>
      <button
        class="btn-pagination"
        :disabled="currentPage === 1"
        @click="currentPage--"
        title="Vai alla pagina precedente"
      >
        <FontAwesomeIcon :icon="faAngleLeft" />
      </button>

      <div class="pagination-jump">
        <span>Pagina</span>
        <input
          type="number"
          v-model="currentPageInput"
          @keyup.enter="jumpToPage"
          @blur="jumpToPage"
          min="1"
          :max="totalPages"
          class="jump-input"
        />
        <span>di {{ totalPages }}</span>
      </div>

      <button
        class="btn-pagination"
        :disabled="currentPage === totalPages"
        @click="currentPage++"
        title="Vai alla pagina successiva"
      >
        <FontAwesomeIcon :icon="faAngleRight" />
      </button>
      <button
        class="btn-pagination"
        :disabled="currentPage === totalPages"
        @click="currentPage = totalPages"
        title="Vai all'ultima pagina"
      >
        <FontAwesomeIcon :icon="faAnglesRight" />
      </button>
    </div>
  </div>

    <!-- Modale Ripristino -->
    <div v-if="showRestoreModal" class="modal-mask">
      <div class="modal">
        <h3>{{ isBulkRestore ? 'Ripristina devices selezionati' : 'Ripristina device' }}</h3>
        <p style="margin: 6px 0 18px">
          <template v-if="selectedIds.length > 1">
            Vuoi ripristinare i <strong>{{ selectedIds.length }}</strong> dispositivi selezionati?
          </template>
          <template v-else>
            Vuoi ripristinare il device
            <strong>{{ selectedDevice?.name || selectedDevice?.id || (selectedIds.length === 1 ? selectedIds[0] : '') }}</strong>?
          </template>
        </p>
        <div class="modal-actions">
          <button :disabled="restoreLoading" @click="confirmRestore">OK</button>
          <button :disabled="restoreLoading" @click="cancelRestore">
            Annulla
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import axios from "@/axiosInstance";
import dayjs from "dayjs";
import { ArchiveRestore as ArchiveRestoreIcon, X as XIcon, ChevronDown as ChevronDownIcon, ChevronUp as ChevronUpIcon } from "lucide-vue-next";
import { computed, inject, onMounted, ref, watch, type Ref } from "vue";
import { RouterLink } from "vue-router";
import { useToast } from "vue-toast-notification";
import NotificationBell from "./NotificationBell.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { 
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight
} from "@fortawesome/free-solid-svg-icons";

const toast = useToast();

const isDesktop = inject<Ref<boolean>>("isDesktop")!;
const isSidebarOpen = inject<Ref<boolean>>("isSidebarOpen")!;
const showCrumbBell = computed(
  () => !isDesktop.value || (isDesktop.value && !isSidebarOpen.value)
);

const loading = ref(true);
const error = ref<string | null>(null);
const archived = ref<any[]>([]);

const searchQuery = ref("");
const currentPage = ref(1);
const itemsPerPage = 10;
const currentPageInput = ref(1);

const sortBy = ref("updatedAt");
const sortOrder = ref<"asc" | "desc">("desc");

const toggleSort = (key: string) => {
  if (sortBy.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortBy.value = key;
    sortOrder.value = "asc";
  }
};

watch(currentPage, (val) => {
  currentPageInput.value = val;
});

const selectedIds = ref<number[]>([]);

const isAllSelected = computed(() => {
  if (paginatedArchived.value.length === 0) return false;
  return paginatedArchived.value.every((d) => selectedIds.value.includes(d.id));
});

function toggleSelectAll() {
  if (isAllSelected.value) {
    const pageIds = paginatedArchived.value.map((d) => d.id);
    selectedIds.value = selectedIds.value.filter((id) => !pageIds.includes(id));
  } else {
    paginatedArchived.value.forEach((d) => {
      if (!selectedIds.value.includes(d.id)) {
        selectedIds.value.push(d.id);
      }
    });
  }
}

watch(searchQuery, () => {
  currentPage.value = 1;
});

const filteredArchived = computed(() => {
  if (!searchQuery.value) return archived.value;
  const q = searchQuery.value.toLowerCase();
  return archived.value.filter((d) => {
    return (
      d.id.toString().toLowerCase().includes(q) ||
      (d.name && d.name.toLowerCase().includes(q)) ||
      (d.status && d.status.toLowerCase().includes(q))
    );
  });
});

const sortedArchived = computed(() => {
  const result = [...filteredArchived.value];
  return result.sort((a, b) => {
    let valA = a[sortBy.value];
    let valB = b[sortBy.value];

    if (sortBy.value === "updatedAt") {
      valA = valA ? new Date(valA).getTime() : 0;
      valB = valB ? new Date(valB).getTime() : 0;
    }

    if (valA < valB) return sortOrder.value === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder.value === "asc" ? 1 : -1;
    return 0;
  });
});

const paginatedArchived = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  return sortedArchived.value.slice(start, start + itemsPerPage);
});

const totalPages = computed(() => {
  return Math.ceil(filteredArchived.value.length / itemsPerPage);
});

const jumpToPage = () => {
  let p = parseInt(String(currentPageInput.value));
  if (isNaN(p)) {
    currentPageInput.value = currentPage.value;
    return;
  }
  if (p < 1) p = 1;
  if (p > totalPages.value) p = totalPages.value;
  currentPage.value = p;
  currentPageInput.value = p;
};

/* ─── stato utente ─── */
const me = ref<any>({});
const isAdmin = computed(() => !!me.value?.is_admin);

function ownerDisplayName(d: any) {
  const u = d.user || {};
  // scegli l’ordine che preferisci: name → username → email → fallback
  return u.name || u.username || u.email || "utente";
}

async function fetchMe() {
  try {
    const { data: user } = await axios.get("/auth/me", {
      withCredentials: true,
    });
    me.value = user;
  } catch (e: any) {
    // se fallisce, mostro un messaggio e continuo (la pagina mostrerà "nessun device")
    error.value = e?.response?.data?.details || "Errore nel recupero utente";
  }
}

async function loadArchived() {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await axios.get("/devices/get-all-graphs", {
      params: { archived: true, all: true }, // <-- admin: tutti gli archiviati
      withCredentials: true,
    });
    archived.value = Array.isArray(data) ? data : [];
  } catch (e: any) {
    error.value = e?.response?.data?.details || "Errore nel caricamento";
  } finally {
    loading.value = false;
  }
}

/* ---------- Ripristino (unarchive) ---------- */
const showRestoreModal = ref(false);
const isBulkRestore = ref(false);
const selectedDevice = ref<any | null>(null);
const restoreLoading = ref(false);

function openRestoreModal(d: any) {
  selectedDevice.value = d;
  isBulkRestore.value = false;
  showRestoreModal.value = true;
}

function openBulkRestoreModal() {
  isBulkRestore.value = true;
  if (selectedIds.value.length === 1) {
    selectedDevice.value = archived.value.find((d) => d.id === selectedIds.value[0]);
  }
  showRestoreModal.value = true;
}

function cancelRestore() {
  showRestoreModal.value = false;
  isBulkRestore.value = false;
  selectedDevice.value = null;
}

async function confirmRestore() {
  if (isBulkRestore.value) {
    await confirmBulkRestore();
    return;
  }
  if (!selectedDevice.value) return;

  restoreLoading.value = true;
  try {
    // endpoint che setta is_archived = false
    await axios.post(
      `/devices/${selectedDevice.value.id}/restore`,
      { is_archived: false },
      { withCredentials: true }
    );

    // aggiorno la lista locale
    archived.value = archived.value.filter(
      (d) => d.id !== selectedDevice.value!.id
    );

    // Notifica chi ascolta (es. DevicesList) di ricaricare
    window.dispatchEvent(
      new CustomEvent("graph-restored", { detail: selectedDevice.value.id })
    );

    toast.success(
      `Device ${
        selectedDevice.value.name || selectedDevice.value.id
      } ripristinato`,
      { position: "top-right", duration: 2000 }
    );

    cancelRestore();
  } catch (e: any) {
    const msg = e?.response?.data?.message || "Errore ripristino";
    toast.error(msg, { position: "top-right", duration: 2500 });
  } finally {
    restoreLoading.value = false;
  }
}

async function confirmBulkRestore() {
  restoreLoading.value = true;
  let successCount = 0;
  let errorCount = 0;

  for (const id of selectedIds.value) {
    try {
      await axios.post(
        `/devices/${id}/restore`,
        { is_archived: false },
        { withCredentials: true }
      );
      successCount++;
    } catch (e) {
      errorCount++;
    }
  }

  if (successCount > 0) {
    archived.value = archived.value.filter(
      (d) => !selectedIds.value.includes(d.id)
    );
    toast.success(`${selectedIds.value.length > 1 ? "Dispositivi" : "Dispositivo"} ripristinat${selectedIds.value.length > 1 ? "i" : "o"} con successo`, {
      position: "top-right",
      duration: 3000,
    });
  }

  if (errorCount > 0) {
    toast.error(`Errore durante il ripristino di ${errorCount} dispositivi`, {
      position: "top-right",
      duration: 3000,
    });
  }

  selectedIds.value = [];
  cancelRestore();
  restoreLoading.value = false;
}

/* ---------- util ---------- */
function formatDate(iso?: string) {
  return iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "—";
}
function listModules(d: any) {
  return (
    d?.nodes
      ?.map((n: any) => n?.label)
      .filter(Boolean)
      .join(", ") || "—"
  );
}
function getStatusClass(status: string): string {
  const map: Record<string, string> = {
    online: "on",
    offline: "off",
    maintenance: "maintenance",
    error: "error",
  };
  return map[status] || "off";
}

/* -------- lifecycle -------- */
onMounted(async () => {
  await fetchMe();
  if (!isAdmin.value) {
    error.value = "Area riservata agli amministratori";
    toast.error(error.value, { position: "top-right", duration: 3000 });
    loading.value = false;
    return;
  }
  await loadArchived();
});
</script>

<style scoped>
/* (stili identici ai tuoi) */
.breadcrumb {
  position: sticky;
  top: 0;
  z-index: 10;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #0d0d0d;
  font: 600 18px/56px "Inter", sans-serif;
  border-bottom: 2px solid transparent;
  border-image: linear-gradient(to bottom, #535454, #2d3e4f) 1;
}
.breadcrumb-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.btn-new {
  padding: 8px 16px;
  background: #008f8f;
  border: 0;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font: 600 14px/1 "Inter", sans-serif;
  transition: background 0.15s;
  text-decoration: none;
}
.btn-new:hover {
  background: #00a7a7;
}
.btn-new:active {
  background: #007777;
}
.msg {
  margin: 24px 0;
  color: #666;
  text-align: center;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
}
.error {
  color: #d00;
}

.owner-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  font: 600 12px/1 Inter, sans-serif;
  border: 1px solid #99a;
  color: #334;
  background: #eef6;
}
.owner-badge.admin {
  border-color: #008f8f;
  color: #008f8f;
  background: color-mix(in srgb, #008f8f 8%, white);
}
.owner-role {
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.owner-sep {
  opacity: 0.5;
}
.owner-id {
  opacity: 0.7;
  font-weight: 500;
}

.dashboard-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 16px;
  box-sizing: border-box;
  padding-bottom: 24px;
}

.tbl-wrap {
  margin-top: 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow-x: auto;
}
.tbl {
  width: 100%;
  border-collapse: collapse;
  font: 14px "Inter", sans-serif;
}
th,
td {
  padding: 10px 16px;
  text-align: left;
  white-space: nowrap;
}
.header-with-action {
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-bulk-action {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}
 .btn-bulk-action svg {
   width: 18px;
   height: 18px;
   stroke: #000;
   transition: 0.2s;
 }
 .btn-bulk-action:hover svg,
 .btn-bulk-action:focus svg {
   stroke: #008f8f;
   transform: scale(1.1);
 }
/* Gmail-style Selection Bar */
.selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border: 1px solid #008f8f;
  border-radius: 8px;
  padding: 8px 16px;
  margin: 0 16px 0;
  box-shadow: 0 4px 12px rgba(0, 143, 143, 0.15);
  animation: slideDown 0.3s ease-out;
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: #333;
}

.btn-close-selection {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.btn-close-selection:hover {
  background: #f0f0f0;
}

.btn-close-selection svg {
  width: 18px;
  height: 18px;
  stroke: #666;
}

.selection-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-selection-action {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #dee2e6;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  color: #444;
  transition: all 0.2s;
}

.btn-selection-action:hover {
  background-color: #f8f9fa;
  border-color: #008f8f;
  color: #008f8f;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-selection-action svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

@keyframes slideDown {
  from { transform: translateY(-10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
.select-col {
  width: 40px;
  text-align: center;
  padding-right: 0;
}
.select-col input {
  cursor: pointer;
  width: 16px;
  height: 16px;
}
thead {
  font-weight: 600;
}

.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable:hover {
  background-color: #f0f0f0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sort-icon {
  width: 14px !important;
  height: 14px !important;
  color: #008f8f;
}
.tbl tbody tr:nth-child(odd) {
  background: #f5f5f5;
}
.tbl tbody tr:nth-child(even) {
  background: #fff;
}
.tbl tbody tr:not(.search-row):hover {
  background: #e0e0e0 !important;
}

.mono {
  font-family: ui-monospace, monospace;
}
.right {
  text-align: right;
}
.actions {
  text-align: right;
}

/***** override azioni tabella *****/
.actions button {
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0 5px;
}
.actions svg {
  width: 18px;
  height: 18px;
  stroke: #000;
  transition: 0.2s;
}
.restore-icon svg:hover {
  stroke: #008f8f;
  transform: scale(1.1);
}
.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 0.5rem;
  box-shadow: 0 0 0 1px #0d0d0d;
  vertical-align: middle;
}
.on {
  background: #27ae60;
  color: #27ae60;
}
.off {
  background: #e74c3c;
  color: #e74c3c;
}
.maintenance {
  background: #f39c12;
  color: #f39c12;
  animation: pulse 2s infinite;
}
.error {
  background: #9b59b6;
  color: #9b59b6;
  animation: pulse 1s infinite;
}
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.modal {
  min-width: 340px;
  max-width: 90%;
  background: #0d0d0d;
  color: #fff;
  padding: 28px 32px;
  border-radius: 8px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
  animation: pop 0.25s ease-out;
}
@keyframes pop {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.modal-actions {
  margin-top: 26px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.modal-actions button {
  min-width: 88px;
  padding: 8px 16px;
  font: 600 14px/1 "Inter", sans-serif;
  border-radius: 4px;
  cursor: pointer;
  color: #fff;
  background: #008f8f;
  border: 1px solid #008f8f;
  transition: background 0.15s;
}
.modal-actions button:hover {
  background: #00a7a7;
}
.modal-actions button:active {
  background: #007777;
}

/* Ricerca */
.search-row {
  background: transparent !important;
}
.search-container {
  padding: 12px 16px;
  background: #f8f8f8;
  border-bottom: 1px solid #ddd;
}
.search-input {
  width: 100%;
  max-width: 300px;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  background: #fff;
  color: #333;
}
.search-input:focus {
  border-color: #008f8f;
}

/* Paginazione */
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 24px;
  padding: 16px;
}
.btn-pagination {
  padding: 6px 16px;
  background: #008f8f;
  border: none;
  color: #fff;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-pagination:hover:not(:disabled) {
  background: #00a7a7;
}
.btn-pagination:disabled {
  background-color: #ccc;
  color: #fff;
  cursor: not-allowed;
}
.pagination-jump {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}
.jump-input {
  width: 50px;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  outline: none;
  background: #fff;
  color: #333;
}
.jump-input:focus {
  border-color: #008f8f;
}
.jump-input::-webkit-outer-spin-button,
.jump-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}
.jump-input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
@media (max-width: 1023px) {
  .breadcrumb {
    padding: 0 45px;
  }
}
</style>
